import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';
import {CMS_RICH_TEXT_WHTML_GRAMMAR_SUMMARY} from '../cms-rich-text-whtml-grammar';

const toolName = 'list-cms-collection-items' as const;

const argumentZod = z.object({
  collectionId: dataToolSchemas.collectionId,
  cmsLocaleId: dataToolSchemas.localeId.optional(),
  limit: z
    .number()
    .optional()
    .describe('Maximum number of items to return (default: 25)'),
  offset: z
    .number()
    .optional()
    .describe('Number of items to skip (default: 0)'),
  sortBy: z
    .string()
    .optional()
    .describe('Field to sort by (e.g. "name", "lastPublished")'),
  sortOrder: z.enum(['asc', 'desc']).optional().describe('Sort direction'),
  slug: z.string().optional().describe('Filter by item slug'),
  name: z.string().optional().describe('Filter by item name'),
  lastPublished: z
    .object({
      gte: z
        .string()
        .optional()
        .describe('Items published on or after this date (ISO 8601 format)'),
      lte: z
        .string()
        .optional()
        .describe('Items published on or before this date (ISO 8601 format)'),
    })
    .optional()
    .describe('Filter by the last published date of the item(s)'),
});

export const listCMSCollectionItemsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'List CMS collection items',
  family: 'cms',
  llmTool: {
    name: toolName,
    description:
      'Lists items in a specific CMS collection with optional filtering and sorting. Returns item data including field values, slugs, and metadata.\n\n' +
      CMS_RICH_TEXT_WHTML_GRAMMAR_SUMMARY,
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [
    {
      resource: 'SiteCollection',
      action: 'read',
      resolveResources: (args) => [
        {type: 'SiteCollection' as const, id: args.collectionId},
      ],
    },
  ],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
});

export type ListCMSCollectionItemsArgumentType = z.infer<typeof argumentZod>;
